/**
 * Measurement-system conversion for recipe display.
 *
 * This is the ONLY place that decides how a quantity is rendered in metric vs
 * imperial. `unitConversion.ts` remains the grocery-matching math (arbitrary
 * unit A → unit B); this module answers a different question: "given the user
 * picked metric, what should this measurement *read* as?".
 *
 * Three rules drive everything here:
 *
 *  1. **Spoons are system-neutral.** tbsp/tsp (and c. à soupe / c. à thé) are
 *     never converted out of, in either direction — nobody measures 7.5 ml of
 *     salt. They are still scaled by the servings ratio.
 *  2. **A cup is 250 ml.** Quebec convention, matching `tasse`. (Note this is
 *     deliberately not the 240 ml US legal cup.)
 *  3. **Temperature is never converted** — see `formatDualTemperature`. It is
 *     shown in both scales at once, independent of the selected system.
 */

import { formatQuantity } from './ingredientParser';

export type MeasurementSystem = 'metric' | 'imperial';

/** A cup is 250 ml here (Quebec `tasse`), not the 240 ml US legal cup. */
export const CUP_ML = 250;
const FLOZ_ML = 30;
const TBSP_ML = 15;
const TSP_ML = 5;
const OZ_G = 28.3495;
const LB_G = 453.592;

/** Quarter cup. Below this, an imperial reader wants spoons, not "0.06 cup". */
const MIN_CUP_ML = CUP_ML / 4;

type UnitKind = 'volume' | 'weight' | 'spoon' | 'other';

interface CanonicalUnit {
  /** Canonical key: ml | l | cup | floz | tbsp | tsp | g | kg | oz | lb | '' */
  key: string;
  kind: UnitKind;
  system: MeasurementSystem | 'neutral';
}

/**
 * Maps every spelling we see in scraped recipes (EN + FR, singular/plural,
 * abbreviated) onto a canonical unit key.
 */
const UNIT_ALIASES: Record<string, CanonicalUnit> = {};

/**
 * One spelling per unit, for registering and for looking up alike. Before, the
 * lookup dropped a trailing period and the table did not, so "c. à s." never
 * matched its own alias; and "c à soupe" (no period), "c.à soupe" and
 * "c. à table" were not spoons at all (#794).
 */
function normalizeUnitKey(unit: string): string {
  return unit
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    // "c. à", "c à", "c.à", "cà" — never "cans" or "cup".
    .replace(/^c(?: ?\. ?| )([àa]) ?|^c(à) ?/, (_m, spaced?: string, glued?: string) => `c. ${spaced ?? glued} `)
    .replace(/\.$/, '');
}

function registerUnit(key: string, kind: UnitKind, system: MeasurementSystem | 'neutral', aliases: string[]) {
  for (const alias of aliases) {
    UNIT_ALIASES[normalizeUnitKey(alias)] = { key, kind, system };
  }
}

registerUnit('ml', 'volume', 'metric', [
  'ml', 'mls', 'millilitre', 'millilitres', 'milliliter', 'milliliters',
]);
registerUnit('l', 'volume', 'metric', [
  'l', 'litre', 'litres', 'liter', 'liters',
]);
// Deliberately no bare 'c' alias: in French "1 c." is a cuillère (spoon), not a
// cup, so treating it as a cup would silently quadruple every French seasoning.
registerUnit('cup', 'volume', 'imperial', [
  'cup', 'cups', 'tasse', 'tasses',
]);
registerUnit('floz', 'volume', 'imperial', [
  'fl oz', 'fl. oz', 'fl.oz', 'floz', 'fluid ounce', 'fluid ounces', 'once liquide', 'onces liquides',
]);
registerUnit('tbsp', 'spoon', 'neutral', [
  'tbsp', 'tbs', 'tbsps', 'tablespoon', 'tablespoons',
  'c. à soupe', 'c. a soupe', 'c. à s.', 'c à soupe', 'cuillère à soupe', 'cuillères à soupe',
  'cuillere a soupe', 'cuilleres a soupe', 'à soupe', 'a soupe',
  'c. à table', 'c. a table', 'cuillère à table', 'cuillères à table', 'cuillere a table', 'cuilleres a table',
]);
registerUnit('tsp', 'spoon', 'neutral', [
  'tsp', 'tsps', 'teaspoon', 'teaspoons',
  'c. à thé', 'c. a the', 'c. à t.', 'c à thé', 'cuillère à thé', 'cuillères à thé',
  'cuillere a the', 'cuilleres a the', 'à thé', 'a the', 'c. à café', 'cuillère à café', 'cuillères à café',
  'à café',
]);
registerUnit('g', 'weight', 'metric', [
  'g', 'gr', 'gram', 'grams', 'gramme', 'grammes',
]);
registerUnit('kg', 'weight', 'metric', [
  'kg', 'kgs', 'kilo', 'kilos', 'kilogram', 'kilograms', 'kilogramme', 'kilogrammes',
]);
registerUnit('oz', 'weight', 'imperial', [
  'oz', 'ozs', 'ounce', 'ounces', 'once', 'onces',
]);
registerUnit('lb', 'weight', 'imperial', [
  'lb', 'lbs', 'pound', 'pounds', 'livre', 'livres',
]);

/**
 * Canonicalize a unit string. Returns kind 'other' for anything we do not
 * convert (counts, "pincée", "gousse", empty) — those pass through untouched.
 */
export function canonicalizeUnit(unit: string | undefined | null): CanonicalUnit {
  if (!unit) return { key: '', kind: 'other', system: 'neutral' };
  const normalized = normalizeUnitKey(unit);
  return UNIT_ALIASES[normalized] ?? { key: '', kind: 'other', system: 'neutral' };
}

/** Display label per canonical key, localized for the spoon units. */
export function unitLabel(key: string, language?: string): string {
  const french = (language ?? '').toLowerCase().startsWith('fr');
  switch (key) {
    case 'ml': return 'ml';
    case 'l': return 'L';
    case 'cup': return french ? 'tasse' : 'cup';
    case 'floz': return french ? 'oz liq.' : 'fl oz';
    case 'tbsp': return french ? 'c. à s.' : 'tbsp';
    case 'tsp': return french ? 'c. à t.' : 'tsp';
    case 'g': return 'g';
    case 'kg': return 'kg';
    case 'oz': return 'oz';
    case 'lb': return 'lb';
    default: return key;
  }
}

/** Convert a canonical volume amount to millilitres. */
function volumeToMl(amount: number, key: string): number | null {
  switch (key) {
    case 'ml': return amount;
    case 'l': return amount * 1000;
    case 'cup': return amount * CUP_ML;
    case 'floz': return amount * FLOZ_ML;
    case 'tbsp': return amount * TBSP_ML;
    case 'tsp': return amount * TSP_ML;
    default: return null;
  }
}

/** Convert a canonical weight amount to grams. */
function weightToG(amount: number, key: string): number | null {
  switch (key) {
    case 'g': return amount;
    case 'kg': return amount * 1000;
    case 'oz': return amount * OZ_G;
    case 'lb': return amount * LB_G;
    default: return null;
  }
}

export interface ConvertedMeasure {
  amount: number;
  /** Canonical unit key of the result. */
  unitKey: string;
  /** True when the unit actually changed (the amount may still have scaled). */
  converted: boolean;
}

/**
 * Render a measurement in the target system.
 *
 * Returns the input untouched when it is already in the target system, when the
 * unit is system-neutral (spoons), or when the unit is not a measure we convert
 * (counts, pinches, cloves).
 *
 * @example
 * convertMeasure(2, 'cups', 'metric')   // { amount: 500, unitKey: 'ml', converted: true }
 * convertMeasure(1, 'tsp', 'metric')    // { amount: 1, unitKey: 'tsp', converted: false }
 * convertMeasure(500, 'g', 'imperial')  // { amount: 17.6…, unitKey: 'oz', converted: true }
 */
export function convertMeasure(
  amount: number,
  unit: string | undefined | null,
  target: MeasurementSystem
): ConvertedMeasure {
  const canonical = canonicalizeUnit(unit);

  // Not a measure we touch, or system-neutral (spoons) — pass straight through.
  if (canonical.kind === 'other' || canonical.kind === 'spoon') {
    return { amount, unitKey: canonical.key, converted: false };
  }
  // Already in the target system.
  if (canonical.system === target) {
    return { amount, unitKey: canonical.key, converted: false };
  }

  if (canonical.kind === 'volume') {
    const ml = volumeToMl(amount, canonical.key);
    if (ml === null) return { amount, unitKey: canonical.key, converted: false };

    if (target === 'metric') {
      return ml >= 1000
        ? { amount: ml / 1000, unitKey: 'l', converted: true }
        : { amount: ml, unitKey: 'ml', converted: true };
    }
    // Imperial: cups down to a quarter cup, then spoons — never "0.06 cup".
    if (ml >= MIN_CUP_ML) return { amount: ml / CUP_ML, unitKey: 'cup', converted: true };
    if (ml >= TBSP_ML) return { amount: ml / TBSP_ML, unitKey: 'tbsp', converted: true };
    return { amount: ml / TSP_ML, unitKey: 'tsp', converted: true };
  }

  if (canonical.kind === 'weight') {
    const g = weightToG(amount, canonical.key);
    if (g === null) return { amount, unitKey: canonical.key, converted: false };

    if (target === 'metric') {
      return g >= 1000
        ? { amount: g / 1000, unitKey: 'kg', converted: true }
        : { amount: g, unitKey: 'g', converted: true };
    }
    const oz = g / OZ_G;
    return oz >= 16
      ? { amount: g / LB_G, unitKey: 'lb', converted: true }
      : { amount: oz, unitKey: 'oz', converted: true };
  }

  return { amount, unitKey: canonical.key, converted: false };
}

/**
 * Format a converted amount for display.
 *
 * The split is weights-and-volumes vs cooking measures, not metric vs imperial:
 * you read a weight off a scale ("0.88 oz"), but you measure a cup or a spoon
 * in fractions ("1 1/2"). Ounces and pounds sit on the decimal side for that
 * reason — rendering 25 g as "7/8 oz" is both unusable and inconsistent with
 * the "7.1 oz" that a larger weight in the same column already produces.
 */
export function formatMeasureAmount(amount: number, unitKey: string): string {
  // Whole millilitres and grams — nobody weighs out 283.5 g.
  if (unitKey === 'ml' || unitKey === 'g') return Math.round(amount).toString();

  // Weights and bulk volumes: decimals. formatQuantity already treats each of
  // these as a decimal unit, so this simply stops routing them to fractions.
  if (unitKey === 'l' || unitKey === 'kg' || unitKey === 'oz' || unitKey === 'lb') {
    return formatQuantity(amount, unitKey);
  }

  // Cups, spoons and fluid ounces are genuinely fractional measures.
  return formatQuantity(amount);
}

/**
 * Which system is this recipe written in? Decided by counting unambiguous
 * signals across the ingredient lines — spoons are neutral and never vote.
 *
 * Defaults to metric on a tie or when nothing votes, matching the app's
 * primary (Quebec) audience.
 */
export function detectRecipeSystem(ingredientTexts: (string | undefined)[]): MeasurementSystem {
  let metric = 0;
  let imperial = 0;

  for (const text of ingredientTexts) {
    if (!text) continue;
    const lower = text.toLowerCase();
    // Word-boundary scan so "g" in "garlic" or "l" in "lait" never votes.
    for (const [alias, canonical] of Object.entries(UNIT_ALIASES)) {
      if (canonical.system === 'neutral') continue;
      const pattern = new RegExp(`(^|[\\s\\d(])${alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[\\s.,;)])`);
      if (pattern.test(lower)) {
        if (canonical.system === 'metric') metric++;
        else imperial++;
      }
    }
  }

  return imperial > metric ? 'imperial' : 'metric';
}

/* ------------------------------------------------------------------ *
 * Temperature — never converted, always shown in both scales.
 * ------------------------------------------------------------------ */

export type TemperatureScale = 'F' | 'C';

const roundTo5 = (n: number) => Math.round(n / 5) * 5;

export function fahrenheitToCelsius(f: number): number {
  return roundTo5(((f - 32) * 5) / 9);
}

export function celsiusToFahrenheit(c: number): number {
  return roundTo5((c * 9) / 5 + 32);
}

/**
 * "375 °F" → "375 °F (190 °C)". The scale as written stays primary; its twin
 * follows in parentheses. Independent of the selected measurement system —
 * a Quebec kitchen runs metric ingredients on a Fahrenheit dial, so both
 * readings are always useful.
 */
export function formatDualTemperature(value: number, scale: TemperatureScale): string {
  if (scale === 'F') {
    return `${value} °F (${fahrenheitToCelsius(value)} °C)`;
  }
  return `${value} °C (${celsiusToFahrenheit(value)} °F)`;
}
